/**
 * MetadataForm Field Renderers
 *
 * Purpose: Reusable field renderer components for different data types
 * Architecture: Individual renderer components for text, currency, date, boolean, list fields
 * Dependencies: Material-UI, date-fns, React, MDM Cache Service
 * Author: Development Team
 * Created: 2025-01-31
 */

import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
  TextField,
  InputAdornment,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useQuery } from '@tanstack/react-query';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLocale } from '../../context/LocaleContext';
import type { FieldRendererProps } from './types';
import {
  scopePickerOptions,
  useMDMCacheInvalidation,
  useMDMScopedOptions,
  useMDMValues,
} from '../../services/mdmCacheService';
import {
  usePickerFilters,
  usePickerLockedValues,
  usePickerScope,
  usePickerTenant,
  usePickerValues,
} from '../../context/PickerScopeContext';
import GroupHeading from '../common/GroupHeading';
import type { MasterDataValue } from '../../types/mdm';
import { HierarchicalFieldRenderer } from '../mdm/HierarchicalFieldRenderer';
import { useAuth } from '../../context/AuthContext';
import { PermissionKey } from '../../constants/permissionKeys';
import { ALL_LABEL, asList, isAll } from '../../utils/listValues';
import {
  SELECT_ALL_LABELS,
  SELECT_ALL_OPTION_ID,
  removeFromAll,
  selectAllMode,
  selectAllState,
  toggleSelectAll,
} from '../../utils/selectAll';
import MultiSelectOption from '../common/MultiSelectOption';
import {
  formatGroupedNumber,
  formatNumberInput,
  getCurrencySymbol,
  getNumberSeparators,
  parseLocaleNumber,
} from '../../utils/formatting';
import { ValueChipColorKey } from '../../theme/orchestrateTheme';
import ValueChip from '../common/ValueChip';
import DescriptionField from '../common/DescriptionField';
import { FileFieldRenderer } from './FileFieldRenderer';
import { usePlanningCurrency } from '../template-form/PlanningCurrencyContext';
import { AddListValueDialog } from './AddListValueDialog';
import { CreatableListbox } from './CreatableListbox';
import ValueFormModal from '../mdm/ValueFormModal';
import { mdmService } from '../../services/mdmService';

// =============================================================================
// TEXT FIELD RENDERER
// =============================================================================

/**
 * Text field renderer
 *
 * Purpose: Renders standard text input field. Honors the field definition's
 *          `is_multiline` flag to render long-form text fields as a textarea
 *          with a max-length character counter (delegated to DescriptionField,
 *          the shared textarea primitive).
 * Input: FieldRendererProps with field configuration and value
 * Output: Material-UI TextField component (single-line) or DescriptionField
 *         (multi-line with counter).
 */
export const TextFieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
  disabled,
  readOnly,
  inputProps,
}) => {
  const label = field.label_override || field.field_definition.label;
  const multiline = field.field_definition.is_multiline === true;

  if (multiline) {
    return (
      <Box>
        <DescriptionField
          label={label}
          value={value || ''}
          onChange={(next) => onChange(next)}
          required={field.is_required}
          error={touched && !!error}
          helperText={touched && error ? error : undefined}
          disabled={disabled || readOnly}
          fullWidth
          margin="none"
          InputProps={{
            readOnly: readOnly,
            inputProps: inputProps,
          }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <TextField
        label={label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        required={field.is_required}
        error={touched && !!error}
        helperText={touched && error}
        disabled={disabled || readOnly}
        fullWidth
        margin="none"
        InputProps={{
          readOnly: readOnly,
        }}
        inputProps={inputProps}
      />
    </Box>
  );
};

// =============================================================================
// CURRENCY/NUMBER FIELD RENDERER
// =============================================================================

/**
 * Count the characters that survive the input mask (digits and the decimal
 * separator) in a slice of text. Group separators are added and removed by the
 * mask, so the caret is anchored to how many of these "kept" characters sit to
 * its left rather than to a raw string offset.
 */
const countMaskedChars = (text: string, decimalSeparator: string): number => {
  let count = 0;
  for (const ch of text) {
    if (/\d/.test(ch) || ch === decimalSeparator) count += 1;
  }
  return count;
};

/** Offset in `text` just after its `count`-th kept character (see countMaskedChars). */
const offsetAfterMaskedChars = (text: string, count: number, decimalSeparator: string): number => {
  if (count <= 0) return text.startsWith('-') ? 1 : 0;
  let seen = 0;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (/\d/.test(ch) || ch === decimalSeparator) {
      seen += 1;
      if (seen === count) return i + 1;
    }
  }
  return text.length;
};

/**
 * Currency field renderer with decimal places handling
 *
 * Purpose: Renders currency/decimal/number input with storage vs display precision
 * Logic:
 * - Displays value rounded to display_decimal_places when not focused, with
 *   trailing zeros stripped (Excel-style "General" format)
 * - Shows storage_decimal_places precision when focused, also with trailing
 *   zeros stripped so a user editing a whole-currency amount sees "1,500"
 *   rather than "1,500.00000"
 * - Applies the locale thousands-separator mask as the user types (ORCH-374):
 *   the input is a text field driven by `formatNumberInput`, and the caret is
 *   kept after the digit just typed when a separator is inserted. The value
 *   handed to `onChange` is always a plain number — separators are display-only.
 * - All formatting and parsing goes through `utils/formatting` so the locale
 *   can be switched centrally (ORCH-191) without touching this renderer.
 * Input: FieldRendererProps with field configuration and value
 * Output: Material-UI TextField with decimal places handling
 */
export const CurrencyFieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
  disabled,
  readOnly,
  inputProps,
}) => {
  const label = field.label_override || field.field_definition.label;
  const datatype = field.field_definition.datatype.toLowerCase();
  const planningCurrencyCode = usePlanningCurrency();

  // Resolve currency symbol from the planning currency code; no currency → no symbol (ORCH-423)
  const currencySymbol = useMemo(
    () => (datatype === 'currency' ? getCurrencySymbol(planningCurrencyCode) : undefined),
    [datatype, planningCurrencyCode]
  );

  // Use resolved decimal places from field (resolved by backend)
  // Priority: field.display_decimal_places (resolved) → field.field_definition.display_decimal_places → default (2)
  const storageDecimalPlaces =
    field.storage_decimal_places ?? field.field_definition?.storage_decimal_places ?? 5;
  const displayDecimalPlaces =
    field.display_decimal_places ?? field.field_definition?.display_decimal_places ?? 2;

  const { decimal: decimalSeparator } = useMemo(() => getNumberSeparators(), []);

  const isNumeric = (candidate: unknown): boolean =>
    candidate !== null && candidate !== undefined && !isNaN(Number(candidate));

  // State for focus/blur behavior
  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState<string>('');

  // Caret restoration after the mask rewrites the text (null = leave the caret alone)
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingCaret = useRef<number | null>(null);

  useLayoutEffect(() => {
    const caret = pendingCaret.current;
    pendingCaret.current = null;
    const input = inputRef.current;
    if (caret === null || !input || document.activeElement !== input) return;
    input.setSelectionRange(caret, caret);
  }, [displayValue]);

  // Format value for display (using display_decimal_places) when not focused
  useEffect(() => {
    if (!isFocused && isNumeric(value)) {
      setDisplayValue(formatGroupedNumber(Number(value), displayDecimalPlaces));
    }
  }, [value, displayDecimalPlaces, isFocused]);

  // Handle focus: show full precision (storage_decimal_places)
  const handleFocus = () => {
    setIsFocused(true);
    if (isNumeric(value)) {
      setDisplayValue(formatGroupedNumber(Number(value), storageDecimalPlaces));
    } else {
      setDisplayValue('');
    }
  };

  // Handle blur: store with storage precision, display with display precision
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    const inputValue = e.target.value;
    if (inputValue === '') {
      onChange(null);
      setDisplayValue('');
      return;
    }

    const numValue = parseLocaleNumber(inputValue);
    if (numValue !== null) {
      // Store with storage_decimal_places precision (preserves full precision up to 5 decimal places)
      const storedValue = Number(numValue.toFixed(storageDecimalPlaces));
      onChange(storedValue);
      // Display rounded to display_decimal_places, trailing zeros stripped
      setDisplayValue(formatGroupedNumber(storedValue, displayDecimalPlaces));
    } else {
      // Invalid input, revert to current value
      if (value !== null && value !== undefined) {
        setDisplayValue(formatGroupedNumber(Number(value), displayDecimalPlaces));
      } else {
        setDisplayValue('');
      }
    }
  };

  // Handle change: re-apply the separator mask and update the value with full precision
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const caret = e.target.selectionStart ?? rawValue.length;
    const keptBeforeCaret = countMaskedChars(rawValue.slice(0, caret), decimalSeparator);

    // Fraction digits are left uncapped while typing (rounded to storage precision on blur);
    // a zero-precision field drops the decimal part entirely.
    const masked = formatNumberInput(rawValue, storageDecimalPlaces > 0 ? Infinity : 0);
    pendingCaret.current = offsetAfterMaskedChars(masked, keptBeforeCaret, decimalSeparator);
    setDisplayValue(masked);

    if (masked === '') {
      onChange(null);
      return;
    }

    const numValue = parseLocaleNumber(masked);
    if (numValue !== null) {
      onChange(numValue); // Store with full precision
    }
  };

  // Determine displayed value
  const getDisplayValue = () => {
    if (isFocused) {
      return displayValue;
    }
    if (isNumeric(value)) {
      return formatGroupedNumber(Number(value), displayDecimalPlaces);
    }
    return '';
  };

  return (
    <Box>
      <TextField
        label={label}
        value={getDisplayValue()}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        required={field.is_required}
        error={touched && !!error}
        helperText={touched && error}
        disabled={disabled || readOnly}
        fullWidth
        margin="none"
        type="text"
        inputRef={inputRef}
        inputProps={{
          inputMode: 'decimal',
          ...(inputProps || {}),
        }}
        InputProps={{
          startAdornment: currencySymbol ? (
            <InputAdornment position="start">{currencySymbol}</InputAdornment>
          ) : undefined,
          readOnly: readOnly,
        }}
      />
    </Box>
  );
};

// =============================================================================
// DATE FIELD RENDERER
// =============================================================================

/**
 * Date field renderer
 *
 * Purpose: Renders date picker with calendar interface
 * Logic: Converts ISO string to Date object for display
 * Input: FieldRendererProps with field configuration and value
 * Output: Material-UI DatePicker component
 */
export const DateFieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
  disabled,
  readOnly,
  inputProps,
  indicatorBorderColor,
}) => {
  const label = field.label_override || field.field_definition.label;
  // ORCH-191: the picker's typed input and placeholder follow the user's locale
  // (dd.MM.yyyy for de-DE); display elsewhere goes through Intl.
  const { dateFnsLocale } = useLocale();

  return (
    <Box>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
        <DatePicker
          label={label}
          value={value ? new Date(value) : null}
          onChange={(date: Date | null) => onChange(date?.toISOString())}
          disabled={disabled || readOnly}
          slotProps={{
            textField: {
              required: field.is_required,
              error: touched && !!error,
              helperText: touched && error,
              fullWidth: true,
              margin: 'none',
              size: 'small',
              InputProps: {
                readOnly: readOnly,
              },
              inputProps: inputProps,
              ...(indicatorBorderColor && {
                sx: {
                  '& .MuiPickersOutlinedInput-root fieldset': { borderColor: indicatorBorderColor },
                  '& .MuiPickersOutlinedInput-root:hover fieldset': {
                    borderColor: indicatorBorderColor,
                  },
                },
              }),
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

// =============================================================================
// BOOLEAN FIELD RENDERER
// =============================================================================

/**
 * Boolean field renderer
 *
 * Purpose: Renders switch toggle for boolean values
 * Logic: Displays switch with label, handles checked state
 * Input: FieldRendererProps with field configuration and value
 * Output: Material-UI FormControlLabel with Switch
 */
export const BooleanFieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  onChange,
  disabled,
  readOnly,
  inputProps,
}) => {
  const label = field.label_override || field.field_definition.label;

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={value || false}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled || readOnly}
            inputProps={inputProps}
          />
        }
        label={label}
      />
    </Box>
  );
};

// =============================================================================
// LIST/DROPDOWN FIELD RENDERER
// =============================================================================

/**
 * List field renderer
 *
 * Purpose: Renders dropdown or multi-select autocomplete with MDM values
 * Logic:
 * - For hierarchical fields: Delegates to HierarchicalFieldRenderer
 * - For flat fields: Fetches options from MDM service, supports single and multi-select
 * Input: FieldRendererProps with field configuration and value
 * Output: Material-UI Autocomplete component populated with MDM values, or HierarchicalFieldRenderer for hierarchical fields
 */
const ADD_NEW_OPTION_ID = '__add_new__';

export const ListFieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
  disabled,
  readOnly,
  indicatorBorderColor,
  inputProps,
  groupBy,
  groupOrder,
  disableClearable,
  selectAll,
}) => {
  const label = field.label_override || field.field_definition.label;
  const isMultiselect = field.field_definition.is_multiselect;
  const { state, can } = useAuth();
  const signedInTenantId = state.user?.activeTenant?.tenant_id;
  const pickerTenant = usePickerTenant();
  const activeTenantId = pickerTenant || signedInTenantId || field.tenant_id;
  // Values are read from another tenant; nothing is created there from a picker
  const otherTenant = !!pickerTenant && pickerTenant !== signedInTenantId;

  // Check if field is hierarchical
  const isHierarchical =
    field.field_definition.hierarchy_config !== null &&
    field.field_definition.hierarchy_config !== undefined;

  // ORCH-128 two-gate inline-create check, mirroring the server (routers/mdm.py, ORCH-334):
  // creation_access 'User' needs mdm.edit or mdm.inline_create; 'Admin' needs mdm.edit;
  // 'None' or missing lets nobody create. A value created in GLOBAL is a GLOBAL write,
  // which always needs mdm.edit (the server's MDM tenant guard).
  const creationAccess = field.field_definition.creation_access;
  const canEditMdm = can(PermissionKey.MDM_EDIT);
  const canCreateUserLevel =
    canEditMdm || (activeTenantId !== 'GLOBAL' && can(PermissionKey.MDM_INLINE_CREATE));
  // ORCH-398: the form limits this field to a fixed set of values
  const limitedTo = usePickerValues(field.fielddef_id);
  const canInlineCreate =
    !readOnly &&
    !disabled &&
    !isHierarchical &&
    !otherTenant &&
    !limitedTo &&
    ((creationAccess === 'User' && canCreateUserLevel) ||
      (creationAccess === 'Admin' && canEditMdm));

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [valueModalOpen, setValueModalOpen] = useState(false);
  const [pendingNewName, setPendingNewName] = useState<string>('');
  // Tracks current input text so we can pre-fill the Add dialog with it.
  // Declared with other hooks to satisfy rules-of-hooks across early returns below.
  const [inputText, setInputText] = useState<string>('');
  const { invalidateFielddef, insertFielddefValue } = useMDMCacheInvalidation();

  // Prefetch the modal-view config when inline create is enabled, so we know at
  // sentinel-click time whether the field needs the full ValueFormModal (sub-model
  // fields) or the simple AddListValueDialog (flat field, name-only).
  const { data: modalViewConfig } = useQuery({
    queryKey: ['mdm-modal-view', field.fielddef_id, activeTenantId],
    queryFn: () => mdmService.getModalView(field.fielddef_id, activeTenantId),
    enabled: canInlineCreate,
    staleTime: 5 * 60 * 1000,
  });
  // A field has sub-model fields when its modal-view returns at least one metadata
  // field row (beyond the fixed name/is_active the MetadataModal always adds).
  const hasSubModelFields = (modalViewConfig?.fields?.length ?? 0) > 0;

  // Fetch MDM values using cache service (skip for hierarchical fields)
  // Use active tenant ID to pick up tenant-level overrides
  const {
    data: allOptions = [],
    isLoading: valuesLoading,
    error: queryError,
  } = useMDMValues(
    field.fielddef_id,
    activeTenantId,
    {}, // Use default filter (active values only)
    { enabled: !isHierarchical } // Skip for hierarchical fields as HierarchicalFieldRenderer handles it
  );
  // ORCH-332: offer only what the surrounding form's permission reaches. Labels still
  // resolve against every visible value, so values already on the record display.
  // ORCH-366: the offer request runs scoped or not; a read-only picker offers nothing.
  const pickerScope = usePickerScope();
  // ORCH-400: narrowed by what the form's related fields hold; stored values this user
  // can't access are locked (kept, no delete icon)
  const filterBy = usePickerFilters(field.fielddef_id);
  const locked = usePickerLockedValues(field.fielddef_id);
  const {
    scope: loadedScope,
    isLoading: scopeLoading,
    isError: scopeError,
  } = useMDMScopedOptions(field.fielddef_id, activeTenantId, pickerScope, {
    enabled: !isHierarchical && !readOnly,
    offer: true,
    filterBy,
  });
  // A failed scoped fetch offers nothing new (fails closed) and says so
  const scope = scopeError ? new Map<string, boolean>() : loadedScope;
  const loading = valuesLoading || scopeLoading;

  const fetchError = queryError
    ? (queryError as Error).message || 'Failed to load options'
    : scopeError
      ? 'Failed to load the values you may choose'
      : null;

  // Delegate to HierarchicalFieldRenderer for hierarchical fields (after hooks)
  if (isHierarchical) {
    return (
      <Box>
        <HierarchicalFieldRenderer
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          touched={touched}
          disabled={disabled}
          readOnly={readOnly}
          indicatorBorderColor={indicatorBorderColor}
          selectAll={selectAll}
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <TextField
          label={label}
          value="Loading..."
          disabled
          fullWidth
          margin="none"
          InputProps={{
            endAdornment: <CircularProgress size={20} />,
          }}
        />
      </Box>
    );
  }

  // Helper function to find option by ID or name
  const findOption = (val: any): MasterDataValue | null => {
    // A single-select field may still hold a one-element array (ORCH-354)
    if (Array.isArray(val)) return findOption(asList(val)[0]);
    if (!val) return null;

    // Try to find by ID first (UUID string)
    const byId = allOptions.find((opt) => opt.id === val);
    if (byId) {
      return byId;
    }

    // If not found by ID, try to find by name (for backward compatibility)
    const byName = allOptions.find((opt) => opt.name === val);
    return byName || null;
  };

  // Helper function to find multiple options by IDs or names
  const findMultipleOptions = (vals: any): MasterDataValue[] => {
    // Handle null/undefined
    if (!vals) return [];

    // Handle single value (string) - wrap in array for multiselect fields
    if (!Array.isArray(vals)) {
      // console.log('[ListFieldRenderer] findMultipleOptions received non-array:', {
      //   value: vals,
      //   type: typeof vals,
      //   fielddef_id: field.fielddef_id,
      //   wrappingInArray: true
      // });
      vals = [vals];
    }

    // Debug logging (disabled in production/tests)
    // console.log('[ListFieldRenderer] findMultipleOptions called with:', {
    //   values: vals,
    //   fielddef_id: field.fielddef_id,
    //   optionsCount: options.length
    // });

    return vals
      .map((val: string) => {
        // Try to find by ID first (UUID string)
        const byId = allOptions.find((opt) => opt.id === val);
        if (byId) return byId;
        // If not found by ID, try to find by name (for backward compatibility with display names)
        const byName = allOptions.find((opt) => opt.name === val);
        return byName || null;
      })
      .filter((opt: MasterDataValue | null): opt is MasterDataValue => opt !== null);
  };

  // Sentinel option for the inline "+ Add new" affordance. Not a real
  // MasterDataValue — detected by id === ADD_NEW_OPTION_ID in onChange.
  const addNewOption = {
    id: ADD_NEW_OPTION_ID,
    name: 'Add New',
  } as unknown as MasterDataValue;

  const openInlineCreate = (prefillName: string) => {
    setPendingNewName(prefillName);
    if (hasSubModelFields) {
      setValueModalOpen(true);
    } else {
      setAddDialogOpen(true);
    }
  };

  const handleOptionSelected = (
    selected: MasterDataValue | MasterDataValue[] | null,
    inputText: string,
    clicked?: MasterDataValue
  ) => {
    // ORCH-418: the "All" row, and picks made while the field is ALL
    if (isMultiselect && clicked && clicked.id !== ADD_NEW_OPTION_ID) {
      if (clicked.id === SELECT_ALL_OPTION_ID) {
        onChange(isAll(value) ? [] : toggleSelectAll(value, visibleOfferedIds, allMode));
        return;
      }
      if (isAll(value)) {
        onChange(removeFromAll(offeredIds, clicked.id));
        return;
      }
    }
    // Detect if the user selected the sentinel "+ Add new" option
    if (isMultiselect && Array.isArray(selected)) {
      const addNewIndex = selected.findIndex((v) => v.id === ADD_NEW_OPTION_ID);
      if (addNewIndex !== -1) {
        const withoutSentinel = selected.filter((v) => v.id !== ADD_NEW_OPTION_ID);
        if (!isAll(value)) onChange(withoutSentinel.map((v) => v.id));
        openInlineCreate(inputText.trim());
        return;
      }
      onChange(selected.map((v) => v.id));
      return;
    }
    if (!isMultiselect) {
      const single = selected as MasterDataValue | null;
      if (single && single.id === ADD_NEW_OPTION_ID) {
        openInlineCreate(inputText.trim());
        return;
      }
      onChange(single ? single.id : null);
    }
  };

  // ORCH-128 §7.3: optimistic insert BEFORE onChange so the Autocomplete can
  // resolve the new id against options synchronously (no blank-field race).
  const handleCreated = (created: MasterDataValue) => {
    insertFielddefValue(field.fielddef_id, activeTenantId, created);
    invalidateFielddef(field.fielddef_id, activeTenantId);
    setAddDialogOpen(false);
    setValueModalOpen(false);
    if (isMultiselect) {
      // ALL already covers a value created now (ORCH-418)
      if (isAll(value)) return;
      onChange([...asList(value), created.id]);
    } else {
      onChange(created.id);
    }
  };

  // A limited field offers a value only when it is in the limit and in scope
  const offered = limitedTo
    ? new Map(
        allOptions.map((option) => [
          option.id,
          (scope ? scope.get(option.id) === true : true) &&
            limitedTo.includes(option.global_value_id || option.id),
        ])
      )
    : scope;
  const { options: scopedOptions, notOffered } = scopePickerOptions(
    allOptions,
    offered,
    asList(value)
  );
  // Grouped lists sort by group (groupOrder first, the rest by name); the sort is
  // stable, so options keep their order within a group
  const groupRank = (group: string): number => {
    const rank = groupOrder?.indexOf(group) ?? -1;
    return rank === -1 ? (groupOrder?.length ?? 0) : rank;
  };
  const options = groupBy
    ? scopedOptions
        .map((option, index) => ({ option, index, group: groupBy(option) }))
        .sort(
          (a, b) =>
            groupRank(a.group) - groupRank(b.group) ||
            a.group.localeCompare(b.group) ||
            a.index - b.index
        )
        .map(({ option }) => option)
    : scopedOptions;

  // ORCH-418: the "All" row. It stores ALL only over the whole list; a search or a
  // narrowed picker (scope, a values limit) stores what it offers.
  const matchesSearch = (option: MasterDataValue) =>
    option.name.toLowerCase().includes(inputText.toLowerCase());
  const offeredIds = options.filter((o) => !notOffered.has(o.id)).map((o) => o.id);
  const visibleOfferedIds = options
    .filter((o) => !notOffered.has(o.id) && matchesSearch(o))
    .map((o) => o.id);
  const narrowed = !!offered && allOptions.some((option) => offered.get(option.id) !== true);
  const allMode = selectAllMode(
    isMultiselect && field.allow_select_all !== false && selectAll !== false && !readOnly,
    narrowed,
    inputText.trim() !== ''
  );
  const allState = selectAllState(value, visibleOfferedIds, allMode);
  const valueIsAll = isMultiselect && isAll(value);
  const selectAllOption = {
    id: SELECT_ALL_OPTION_ID,
    name: allMode === 'off' ? ALL_LABEL : SELECT_ALL_LABELS[allMode],
  } as unknown as MasterDataValue;
  // The value the Autocomplete holds: ALL shows as one "All" chip
  const allChipOption = { id: SELECT_ALL_OPTION_ID, name: ALL_LABEL } as unknown as MasterDataValue;

  // ORCH-400 decision 9: a locked value is never dropped by this user. Backspace, the
  // clear button and unticking it put it back; a locked single-select can be replaced
  // (decision 12) but not cleared.
  const lockedIds = asList(value).filter((id) => locked.has(String(id)));
  const keepLocked = (ids: unknown[]): unknown[] => [
    ...ids,
    ...lockedIds.filter((id) => !ids.includes(id)),
  ];
  const singleLocked = !isMultiselect && lockedIds.length > 0;
  const lockedHelper = lockedIds.length
    ? `${lockedIds
        .map((id) => findOption(id)?.name ?? String(id))
        .join(
          ', '
        )} ${lockedIds.length === 1 ? 'is' : 'are'} outside your access and can't be removed.`
    : undefined;

  return (
    <Box>
      <Autocomplete
        multiple={isMultiselect}
        // ORCH-418: a multi-select stays open while the user picks
        disableCloseOnSelect={isMultiselect}
        options={options}
        // The Add New sentinel and the All row stay out of every group
        groupBy={
          groupBy
            ? (option: MasterDataValue) =>
                option.id === ADD_NEW_OPTION_ID || option.id === SELECT_ALL_OPTION_ID
                  ? ''
                  : groupBy(option)
            : undefined
        }
        // The shared grouped-list heading (ORCH-410): the same band RoleSelect uses
        renderGroup={
          groupBy
            ? (params) =>
                params.group ? (
                  <GroupHeading
                    key={params.key}
                    group={params.group}
                    top={canInlineCreate ? 36 : 0}
                    dataTestId={`option-group-${field.fielddef_id}`}
                  >
                    {params.children}
                  </GroupHeading>
                ) : (
                  <li key={params.key}>
                    <Box component="ul" sx={{ p: 0 }}>
                      {params.children}
                    </Box>
                  </li>
                )
            : undefined
        }
        disableClearable={disableClearable || singleLocked}
        // Already on the record but outside the form's scope: shown, not offered again
        getOptionDisabled={(option: MasterDataValue) => notOffered.has(option.id)}
        getOptionLabel={(option: MasterDataValue) => option.name}
        filterOptions={(opts, params) => {
          const filtered = opts.filter((opt) =>
            opt.name.toLowerCase().includes(params.inputValue.toLowerCase())
          );
          // The All row goes first (below Add New) while anything visible is selectable
          const withAll =
            allMode !== 'off' && filtered.some((opt) => !notOffered.has(opt.id))
              ? [selectAllOption, ...filtered]
              : filtered;
          if (canInlineCreate) {
            // Prepend sentinel so it is the first option; CreatableListbox makes it sticky.
            return [addNewOption, ...withAll];
          }
          return withAll;
        }}
        // ORCH-128: the sticky listbox is wired through slotProps.listbox.component,
        // MUI's documented replacement for the deprecated ListboxComponent prop.
        // MUI 7.3.11 destructures ListboxComponent but never applies it, so on a
        // build that resolves a newer MUI the custom <ul> silently disappears and
        // MUI's own listbox (8px top padding) renders a gap above "Add New".
        slotProps={{ listbox: canInlineCreate ? { component: CreatableListbox } : undefined }}
        renderOption={(props, option, state) => {
          if (option.id === ADD_NEW_OPTION_ID) {
            return (
              <Box
                component="li"
                {...props}
                key={ADD_NEW_OPTION_ID}
                data-testid={`add-new-option-${field.fielddef_id}`}
                sx={{
                  position: 'sticky !important',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'primary.main',
                  fontWeight: 500,
                }}
              >
                <AddIcon fontSize="small" />
                {option.name}
              </Box>
            );
          }
          if (isMultiselect) {
            const { key, ...liProps } = props as typeof props & { key: string };
            if (option.id === SELECT_ALL_OPTION_ID) {
              return (
                <MultiSelectOption
                  key={SELECT_ALL_OPTION_ID}
                  {...liProps}
                  label={option.name}
                  selected={allState === 'all'}
                  indeterminate={allState === 'some'}
                  dataTestId="list-select-all-option"
                />
              );
            }
            return (
              <MultiSelectOption
                key={key}
                {...liProps}
                label={option.name}
                selected={valueIsAll || state.selected}
              />
            );
          }
          return (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          );
        }}
        value={
          isMultiselect
            ? valueIsAll
              ? [allChipOption]
              : findMultipleOptions(value)
            : findOption(value)
        }
        // A multi-select keeps its search text while the user ticks several matches: MUI
        // resets it on every pick (reason selectOption / removeOption / reset); only typing
        // and the clear button change it, and closing the list empties it
        inputValue={isMultiselect ? inputText : undefined}
        onInputChange={(_, newInputValue, reason) => {
          if (isMultiselect && reason !== 'input' && reason !== 'clear') return;
          setInputText(newInputValue);
        }}
        onClose={() => {
          if (isMultiselect) setInputText('');
        }}
        onChange={(_, newValue, reason, details) => {
          if (isMultiselect && reason === 'clear') {
            onChange(keepLocked([]));
            return;
          }
          if (isMultiselect && lockedIds.length && Array.isArray(newValue)) {
            const picked = newValue as MasterDataValue[];
            if (picked.some((o) => o.id === ADD_NEW_OPTION_ID || o.id === SELECT_ALL_OPTION_ID)) {
              handleOptionSelected(newValue as any, inputText, details?.option);
              return;
            }
            onChange(keepLocked(picked.map((o) => o.id)));
            return;
          }
          if (singleLocked && (newValue === null || newValue === undefined)) return;
          handleOptionSelected(newValue as any, inputText, details?.option);
        }}
        disabled={disabled || readOnly}
        readOnly={readOnly}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={field.is_required}
            error={touched && (!!error || !!fetchError)}
            // The field's own help text (fields.description_override), unless an error shows
            helperText={
              (touched && (error || fetchError)) ||
              lockedHelper ||
              field.description_override ||
              undefined
            }
            fullWidth
            margin="none"
            inputProps={{ ...params.inputProps, ...(inputProps || {}) }}
          />
        )}
        renderTags={(tagValue, getTagProps) => {
          return tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            const chipColorKey = option.chip_color;
            if (option.id === SELECT_ALL_OPTION_ID) {
              return (
                <ValueChip
                  key={key}
                  label={ALL_LABEL}
                  size="small"
                  data-testid="list-all-chip"
                  {...tagProps}
                />
              );
            }

            // A locked chip has no delete icon (the WorkflowStepModal pattern)
            const { onDelete, ...keptProps } = tagProps;
            return (
              <ValueChip
                key={key}
                label={option.name}
                chipColor={chipColorKey as ValueChipColorKey | null | undefined}
                size="small"
                data-testid={locked.has(option.id) ? 'list-locked-chip' : undefined}
                {...(locked.has(option.id) ? keptProps : { ...keptProps, onDelete })}
              />
            );
          });
        }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
      {canInlineCreate && (
        <AddListValueDialog
          open={addDialogOpen}
          fielddefId={field.fielddef_id}
          tenantId={activeTenantId}
          fieldLabel={label}
          initialName={pendingNewName}
          onCancel={() => setAddDialogOpen(false)}
          onCreated={handleCreated}
        />
      )}
      {canInlineCreate && hasSubModelFields && (
        <ValueFormModal
          open={valueModalOpen}
          onClose={() => setValueModalOpen(false)}
          fielddef_id={field.fielddef_id}
          tenant_id={activeTenantId}
          fieldDefinition={field.field_definition as any}
          onSuccess={handleCreated}
          creatorMode
        />
      )}
    </Box>
  );
};

// =============================================================================
// FIELD RENDERER MAPPING
// =============================================================================

/**
 * Field renderer mapping
 *
 * Purpose: Maps datatype strings to appropriate renderer components
 * Logic: Provides default renderers for all supported data types
 */
export const fieldRenderers: Record<string, React.FC<FieldRendererProps>> = {
  text: TextFieldRenderer,
  string: TextFieldRenderer,
  currency: CurrencyFieldRenderer,
  decimal: CurrencyFieldRenderer,
  number: CurrencyFieldRenderer,
  date: DateFieldRenderer,
  datetime: DateFieldRenderer,
  boolean: BooleanFieldRenderer,
  bool: BooleanFieldRenderer,
  list: ListFieldRenderer,
  file: FileFieldRenderer,
};

/**
 * Get appropriate renderer for field datatype
 *
 * Purpose: Returns renderer component for given datatype
 * Logic: Looks up renderer in mapping, defaults to TextFieldRenderer
 * Input: datatype - field datatype string
 * Output: React component for rendering the field
 */
export const getFieldRenderer = (datatype: string): React.FC<FieldRendererProps> => {
  return fieldRenderers[datatype.toLowerCase()] || TextFieldRenderer;
};
